/**
 * One <main> landmark per page. AppRouter's wrapper is the main landmark for pages without a shell
 * (sign-in, classic view). The customer and admin shells render their own content <main>, and the
 * wrapper used to stay a <main> around them, so admin pages had three nested mains (with
 * ui/sidebar's SidebarInset), which breaks screen readers' "jump to main" and landmark lists.
 * A shell calls useClaimMainLandmark(); while any shell is mounted the wrapper drops its role.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

type Claim = () => () => void;
const MainLandmarkContext = createContext<Claim | null>(null);

/** Renders the app wrapper: role="main" unless a shell below has claimed the landmark. */
export function MainLandmarkWrapper({ className, children }: { className?: string; children: ReactNode }) {
  const [claims, setClaims] = useState(0);
  const [claim] = useState<Claim>(() => () => {
    setClaims((n) => n + 1);
    return () => setClaims((n) => n - 1);
  });
  return (
    <MainLandmarkContext.Provider value={claim}>
      {/* Same element either way, so toggling the role never remounts the app. */}
      <div className={className} role={claims > 0 ? undefined : 'main'}>{children}</div>
    </MainLandmarkContext.Provider>
  );
}

/** Call in a component that renders the page's real content <main>. */
export function useClaimMainLandmark(): void {
  const claim = useContext(MainLandmarkContext);
  useEffect(() => (claim ? claim() : undefined), [claim]);
}
